// src/components/common/StatCard.js
import { TrendingDown, TrendingUp } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

// Animates a numeric value counting up from its previous value to the new
// one on every change (including first mount, from 0) — a small touch that
// makes the dashboard feel alive instead of numbers just appearing.
// Non-numeric values (e.g. "No data") pass through unanimated.
const useCountUp = (value, durationMs = 700) => {
  const [display, setDisplay] = useState(typeof value === 'number' ? 0 : value);
  const fromRef = useRef(0);
  const frameRef = useRef();

  useEffect(() => {
    if (typeof value !== 'number') {
      setDisplay(value);
      return;
    }
    const from = fromRef.current;
    const to = value;
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - start) / durationMs, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setDisplay(Math.round(from + (to - from) * eased));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
      }
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return display;
};

const StatCard = ({
  title,
  value,
  change,
  icon: IconComponent,
  trend = 'up',
  color = 'blue',
  loading = false,
  onClick,
  delay = 0,
  className = ''
}) => {
  const animatedValue = useCountUp(value);
  const colorClasses = {
    blue: 'from-primary-500 to-accent-600',
    green: 'from-green-500 to-emerald-600',
    yellow: 'from-yellow-500 to-orange-600',
    red: 'from-red-500 to-pink-600',
    purple: 'from-accent-500 to-indigo-600'
  };

  const trendColorClasses = {
    up: 'text-emerald-600 dark:text-emerald-400',
    down: 'text-red-500 dark:text-red-400',
    neutral: 'text-gray-500 dark:text-gray-400'
  };

  if (loading) {
    return (
      <div className={`relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 ${className}`}>
        <div className="flex items-start justify-between">
          <div className="flex-1 space-y-2">
            <div className="skeleton h-4 w-24"></div>
            <div className="skeleton h-8 w-16"></div>
            <div className="skeleton h-3 w-32"></div>
          </div>
          <div className="skeleton w-12 h-12 rounded-xl"></div>
        </div>
      </div>
    );
  }

  const CardContent = () => (
    <div
      style={{ animationDelay: `${delay}ms` }}
      className="group relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 hover:shadow-xl dark:hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] hover:-translate-y-0.5 animate-fade-in-up"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-accent-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      <div className="relative z-10">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
              {title}
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mb-2 tabular-nums">
              {typeof animatedValue === 'number' ? animatedValue.toLocaleString() : animatedValue}
            </p>
            {change && (
              <div className={`flex items-center text-xs font-semibold ${trendColorClasses[trend]}`}>
                {trend === 'up' && <TrendingUp className="h-3 w-3 mr-1" />}
                {trend === 'down' && <TrendingDown className="h-3 w-3 mr-1" />}
                <span>{change}</span>
              </div>
            )}
          </div>
          {IconComponent && (
            <div className={`p-3 bg-gradient-to-br ${colorClasses[color]} rounded-xl shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300`}>
              <IconComponent className="h-6 w-6 text-white" />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (onClick) {
    return (
      <button 
        onClick={onClick}
        className={`w-full text-left focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-2xl ${className}`}
      >
        <CardContent />
      </button>
    );
  }

  return <div className={className}><CardContent /></div>;
};

export default StatCard;